import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { cn } from '@/lib/utils';
import { ticketPath, oppPath, projectPath, projectTaskPath } from '@/lib/routes';

marked.setOptions({ gfm: true, breaks: true });

// Record deep-links the assistant produces (e.g. https://cosmos.synfo.com.au/psa/opportunities/123).
// They're absolute so they can be copied and shared, which would otherwise mean a full page
// reload — clicks on them are intercepted below and handed to the router instead.
//
// The `/psa/` prefix is optional here on purpose: records moved under /psa, but a link copied out
// of an older chat still carries the flat path. Both shapes are matched and normalised to the
// current one before navigating. The emitting half of this contract is OPP_LINK_RULE in
// server/routes.ts — change both together or links render but stop routing in-app.
const APP_RECORD_PATH = /^\/(?:psa\/)?(opportunities|tickets|projects)\/(\d+)(?:\/tasks\/(\d+))?$/;
const APP_HOSTS = new Set(['cosmos.synfo.com.au']);

/**
 * Render trusted-but-sanitised markdown (e.g. an AI reply) as styled HTML, and add a
 * one-click "Copy" button to every fenced code block. The assistant is instructed to wrap
 * customer-facing drafts in a ```draft block — those render as normal prose (not monospace)
 * but are still copyable in one click.
 */
export function Markdown({ content, className }: { content: string; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const html = useMemo(
    () => DOMPurify.sanitize(marked.parse(content, { async: false }) as string),
    [content],
  );

  // Keep in-app record links inside the SPA. Anything else (external links, modified clicks)
  // falls through to the browser untouched.
  const onClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    const anchor = (e.target as HTMLElement).closest('a');
    const href = anchor?.getAttribute('href');
    if (!href) return;
    let url: URL;
    try { url = new URL(href, window.location.origin); } catch { return; }
    const isAppHost = url.origin === window.location.origin || APP_HOSTS.has(url.hostname);
    if (!isAppHost) return;
    const match = APP_RECORD_PATH.exec(url.pathname);
    if (!match) return;
    const [, entity, id, taskId] = match;
    if (taskId && entity !== 'projects') return;
    e.preventDefault();
    // Normalise, so a flat legacy link lands on the current route rather than bouncing
    // through the redirect. Projects links come from the Projects AI (PROJECT link rule in
    // server/psa/projectAssistant.ts) — change both halves together.
    navigate(entity === 'tickets' ? ticketPath(id) : entity === 'projects' ? (taskId ? projectTaskPath(id, taskId) : projectPath(id)) : oppPath(id));
  }, [navigate]);

  // dangerouslySetInnerHTML produces static HTML, so wire up Copy buttons against the live DOM.
  // Re-runs whenever the rendered HTML changes (incl. each streaming delta) — React replaces
  // innerHTML on each change, so buttons are simply re-added to the fresh nodes.
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    root.querySelectorAll('pre').forEach((pre) => {
      // Already enhanced? (defensive — innerHTML is normally replaced wholesale on change)
      if ((pre.parentElement as HTMLElement | null)?.dataset.codewrap) return;

      const code = pre.querySelector('code');
      if (code?.classList.contains('language-draft')) pre.setAttribute('data-draft', 'true');

      // Wrap the <pre> in a relative, non-scrolling container and hang the button off THAT,
      // so the button stays visible top-right regardless of horizontal scroll inside the <pre>.
      const wrap = document.createElement('div');
      wrap.dataset.codewrap = 'true';
      wrap.className = 'relative my-2';
      pre.before(wrap);
      wrap.appendChild(pre);
      pre.style.margin = '0';

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.textContent = 'Copy';
      btn.className =
        'absolute right-1.5 top-1.5 z-10 rounded border border-border bg-background/90 px-1.5 py-0.5 text-[11px] font-sans text-muted-foreground hover:bg-accent hover:text-foreground';
      btn.addEventListener('click', async () => {
        try {
          await navigator.clipboard.writeText((code ?? pre).innerText);
          btn.textContent = 'Copied';
          setTimeout(() => { btn.textContent = 'Copy'; }, 1500);
        } catch {
          btn.textContent = 'Failed';
        }
      });
      wrap.appendChild(btn);
    });

    // Tables can be wider than the panel they're rendered in (the ticket/opportunity side rail
    // is ~300px), so give each one its own horizontal scroller rather than letting it squash
    // the columns or push the chat bubble wide.
    root.querySelectorAll('table').forEach((table) => {
      if ((table.parentElement as HTMLElement | null)?.dataset.tablewrap) return;
      const wrap = document.createElement('div');
      wrap.dataset.tablewrap = 'true';
      wrap.className = 'my-2 max-w-full overflow-x-auto';
      table.before(wrap);
      wrap.appendChild(table);
    });
  }, [html]);

  return (
    <div
      ref={ref}
      onClick={onClick}
      className={cn(
        'text-sm leading-relaxed break-words',
        '[&_p]:my-1.5 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0',
        '[&_ul]:my-1.5 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-1.5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0.5',
        '[&_h1]:text-base [&_h1]:font-semibold [&_h1]:mt-2 [&_h1]:mb-1',
        '[&_h2]:text-sm [&_h2]:font-semibold [&_h2]:mt-2 [&_h2]:mb-1',
        '[&_h3]:text-sm [&_h3]:font-semibold [&_h3]:mt-2 [&_h3]:mb-1',
        '[&_strong]:font-semibold [&_a]:text-primary [&_a]:underline',
        '[&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[0.85em]',
        '[&_pre]:my-2 [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:bg-muted [&_pre]:p-2.5 [&_pre]:pr-12 [&_pre]:text-[0.85em]',
        '[&_pre_code]:bg-transparent [&_pre_code]:p-0',
        // Draft blocks: read as prose (sans-serif, wrapped) but stay copyable.
        '[&_pre[data-draft=true]]:whitespace-pre-wrap [&_pre[data-draft=true]]:text-sm [&_pre[data-draft=true]_code]:font-sans',
        '[&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-3 [&_blockquote]:text-muted-foreground',
        // Tables: fill a wide container, but hold a minimum width in a narrow one and let the
        // wrapper scroll (see the table wrapping in the effect above) rather than squashing.
        '[&_table]:my-0 [&_table]:w-full [&_table]:min-w-[420px] [&_table]:text-[0.92em] [&_table]:border-collapse',
        '[&_th]:border-b [&_th]:py-1 [&_th]:pr-3 [&_th]:text-left [&_th]:font-semibold [&_th]:whitespace-nowrap',
        '[&_td]:border-b [&_td]:border-border/50 [&_td]:py-1 [&_td]:pr-3 [&_td]:align-top',
        '[&_td:first-child]:font-medium',
        className,
      )}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
